import {
  rem,
  Theme,
  TmElement,
  spacing,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../helpers';

const tag = tagName('accordion');
export const theme = new Theme(tag);

const padding = theme.var('padding', spacing(150));

@customElement(tag)
export class AccordionComponent extends TmElement {
  static override styles = [
    css`
      :host {
        box-sizing: border-box;
        display: block;
        inline-size: 100%;
      }

      a-icon {
        font-size: ${rem(12)};
      }

      :where(details) {
        overflow: hidden;
      }

      :where(summary) {
        align-items: center;
        cursor: pointer;
        display: flex;
        gap: ${padding};
        padding: ${padding};

        &:hover {
          background: ${token('accordion-background-default')};

          :where(a-icon) {
            color: var(--chevron-color, ${token('accordion-foreground-hover')});
          }
        }

        &::-webkit-details-marker {
          display: none;
        }

        &:focus-visible {
          outline-color: var(--tm-focus-ring-color);
          outline-offset: calc(-1 * var(--tm-focus-ring-width));
          outline-style: var(--tm-focus-ring-style);
          outline-width: var(--tm-focus-ring-width);
        }

        :where(a-icon) {
          color: var(--chevron-color, ${token('accordion-foreground-default')});
        }

        ::slotted(*) {
          flex: 1;
          user-select: none;
        }
      }

      :where(summary.hostContainsComponent) {
        display: flex;
        gap: 0;
        padding: 0;

        &:hover {
          background: unset;
        }
      }

      :where(div.hostContainsComponent) {
        align-self: stretch;
        display: flex;
        inline-size: ${rem(40)};
        justify-content: center;

        &:hover {
          background: var(
            --hover-color,
            ${token('accordion-chevron-background-hover')}
          );
        }
      }

      :where(div.hostDoesNotContainComponent) {
        display: flex;
      }

      :where([part='chevron']) {
        align-items: var(--chevron-align-items, center);
        padding-block-start: var(--chevron-padding-block-start, 0);
      }

      summary :where([part='chevron']) {
        padding-inline-end: var(--chevron-padding, 0);
      }

      :where(details[open]) summary :where(a-icon[name='chevron-down']) {
        display: none;
      }

      :where(details:not([open])) summary :where(a-icon[name='chevron-up']) {
        display: none;
      }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  open = false;

  hostContainsComponent = () => {
    const relevantElement = this.shadowRoot?.host.querySelector(
      'a-button, a-checkbox, a-toc-action',
    );
    return !!relevantElement;
  };

  override render() {
    const hostContainsComponentClass = this.hostContainsComponent()
      ? 'hostContainsComponent'
      : 'hostDoesNotContainComponent';

    return html`<details
      ?open=${this.open}
      part="details"
      data-test="accordion-details"
    >
      <summary
        part="summary"
        class=${hostContainsComponentClass}
        data-test="accordion-summary"
        appAnalyseClick="accordion-chevron"
      >
        <slot name="summary" @slotchange=${this.onSlotChange}></slot>
        <div
          part="chevron"
          class=${hostContainsComponentClass}
          tabindex="-1"
          appAnalyseClick="click-chevron-toc"
        >
          <a-icon
            name="chevron-down"
            id="chevron-down"
            part="chevron-icon"
          ></a-icon>
          <a-icon
            name="chevron-up"
            id="chevron-up"
            part="chevron-icon"
          ></a-icon>
        </div>
      </summary>
      <div part="main"><slot></slot></div>
    </details>`;
  }

  onSlotChange() {
    // Force update when slot changes
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-accordion': AccordionComponent;
  }
}
